import {
  Box,
  Button,
  Flex,
  Link as ChakraLink,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useCallback, useMemo, useState } from "react";
import { MdAssignment, MdChevronRight, MdHomeWork, MdQuiz } from "react-icons/md";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { studentPalette } from "../../constants/studentPalette";
import { studentSurfaces } from "../../constants/studentThemeSurfaces";
import { useStartsInLabel, useExamEndSchedule } from "../../hooks/useStartsInLabel";
import { openSolveHomeworkModel } from "../../modules/admin-modules/models/Actions";
import {
  examRedoRequest,
  getAllExamsAndHomeworksRequest,
} from "../../modules/students-modules/exams/Actions";
import SolveHomework from "../../containers/student/models/solve-homework";
import PdfPreviewModal, {
  examSubmissionPdfUrl,
  homeworkSubmissionPdfUrl,
} from "./PdfPreviewModal";

const C = {
  primary: studentPalette.primary,
  secondary: studentPalette.secondary,
  border: studentSurfaces.border,
  paper: studentSurfaces.raised,
  muted: studentSurfaces.textMuted,
};

const thinScrollCss = {
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(91, 13, 22, 0.35) transparent",
  "&::-webkit-scrollbar": { width: "4px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(91, 13, 22, 0.35)",
    borderRadius: "999px",
  },
  "&::-webkit-scrollbar-button": { display: "none", height: 0, width: 0 },
};

const darkTitleSx = { "html[data-theme='dark'] &": { color: "var(--text-primary)" } };

const primaryBtnProps = {
  size: "xs",
  bg: C.secondary,
  color: "white",
  fontWeight: "800",
  textTransform: "uppercase",
  fontSize: "10px",
  borderRadius: "lg",
  _hover: { bg: C.primary },
  flexShrink: 0,
};

const outlineBtnProps = {
  size: "xs",
  variant: "outline",
  borderWidth: "2px",
  borderColor: C.primary,
  color: C.primary,
  fontWeight: "800",
  textTransform: "uppercase",
  fontSize: "10px",
  borderRadius: "lg",
  _hover: { bg: "rgba(91, 13, 22, 0.06)" },
  flexShrink: 0,
};

const isQuizType = (ex) => Number(ex?.type) === 0;

const isFormalExamType = (ex) => {
  const t = Number(ex?.type);
  return t === 1 || t === 6;
};

const isHomeworkExamType = (ex) => Number(ex?.type) === 2;

const PANEL_CONFIG = {
  quizzes: {
    eyebrow: "Quizzes",
    title: "Short assessments",
    icon: MdQuiz,
    viewAllTo: "/students/quick-links?tab=quizzes",
    empty: "No quizzes yet.",
  },
  exams: {
    eyebrow: "Exams",
    title: "Formal exams",
    icon: MdAssignment,
    viewAllTo: "/students/quick-links?tab=exams",
    empty: "No exams yet.",
  },
  homework: {
    eyebrow: "Homeworks",
    title: "Assignments & classified",
    icon: MdHomeWork,
    viewAllTo: "/students/quick-links?tab=homework",
    empty: "No homework or classified exams yet.",
  },
};

const sortByNewest = (items) =>
  [...items].sort((a, b) => {
    const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });

function itemTitle(item) {
  return item?.title || item?.name || "Untitled";
}

function examTypeLabel(item) {
  const t = Number(item?.type);
  if (t === 2) return "Homework/Classified";
  if (t === 0) return "Quiz";
  return "Exam";
}

function formatExamMeta(item) {
  const n =
    item?.total_count ??
    item?.questions_count ??
    item?.questions ??
    item?.question_count ??
    item?.no_of_questions;
  const timed = item?.timer == 1 && item?.time != null && String(item.time).trim() !== "";
  const pts = item?.score;
  const hasPts = pts != null && String(pts).trim() !== "" && Number(pts) > 0;
  const parts = [];
  if (n != null && String(n).trim() !== "") parts.push(`${n} questions`);
  if (timed) parts.push(`${item.time} min`);
  if (hasPts) parts.push(`${pts} pts`);
  if (item?.created_at) {
    parts.push(dayjs(item.created_at).format("D MMM").toUpperCase());
  }
  return parts.length ? `${examTypeLabel(item)} • ${parts.join(" • ")}` : examTypeLabel(item);
}

function formatHomeworkMeta(item) {
  const d = item?.created_at;
  return d ? `Homework • ${dayjs(d).format("D MMM YYYY")}` : "Homework";
}

function homeworkActionLabel(hw) {
  const sh = hw?.student_homework;
  const hasFile = sh?.file;
  const status = sh?.status;
  if ((status === 0 || status === 1) && hasFile) return "Preview";
  if (status === 2) return "Resubmit";
  return "Solve";
}

function isExamCorrected(ex) {
  return Number(ex?.correction) === 1;
}

function isHomeworkCorrected(hw) {
  return Number(hw?.student_homework?.correction ?? hw?.correction) === 1;
}

function hasRightAnswers(item) {
  const value = item?.right_answers ?? item?.student_homework?.right_answers;
  return value != null && String(value).trim() !== "";
}

function updatedAtMs(item) {
  const raw =
    item?.updated_at ??
    item?.student_homework?.updated_at ??
    item?.created_at;
  const t = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
}

function homeworkStatusSummary(hw) {
  const sh = hw?.student_homework;
  if (!sh) return null;
  if (sh.status === 0) return "Pending review";
  if (sh.status === 1) return "Accepted";
  if (sh.status === 2) return "Refused — you can resubmit";
  return "Submitted";
}

function RowShell({ icon: Icon, title, subtitle, children }) {
  return (
    <Flex
      direction="column"
      align="stretch"
      gap={2.5}
      p={3}
      bg={studentSurfaces.muted}
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="md"
    >
      <Flex align="flex-start" gap={3} minW={0}>
        <Box color={C.primary} flexShrink={0} sx={darkTitleSx} mt="2px">
          <Icon size={18} />
        </Box>
        <Box flex="1" minW={0}>
          <Text
            fontSize="sm"
            fontWeight="700"
            color={C.secondary}
            noOfLines={2}
            sx={darkTitleSx}
          >
            {title}
          </Text>
          {subtitle ? (
            <Text fontSize="xs" color={C.muted} fontWeight="600" mt={0.5} noOfLines={2}>
              {subtitle}
            </Text>
          ) : null}
        </Box>
      </Flex>
      {children ? (
        <Flex gap={2} flexWrap="wrap" align="center">
          {children}
        </Flex>
      ) : null}
    </Flex>
  );
}

function ExamRow({
  ex,
  courseReturnPath,
  redoLoadingExamId,
  onExamPreview,
  onRequestRedo,
}) {
  const startsIn = useStartsInLabel(ex?.start_time, 1000);
  const { pastEnd, closesIn } = useExamEndSchedule(ex?.end_time, 1000);
  const examGuideQs = new URLSearchParams({ name: itemTitle(ex) });
  if (courseReturnPath) examGuideQs.set("returnTo", courseReturnPath);
  const examResultQs = new URLSearchParams({ name: itemTitle(ex) });
  const examSubmitted =
    ex?.student_pdf != null && String(ex.student_pdf).trim() !== "";
  const examGraded = ex?.right_answers != null;
  const canReviewAnswers = examGraded && ex?.correction == 1;
  const examResultPath = `/exam-result/${ex.id}?${examResultQs.toString()}`;
  const examGuidePath = `/solve-exam/${ex.id}?${examGuideQs.toString()}`;
  const scoreDenom = ex?.score ?? ex?.total_count;
  const showMarkLine = examSubmitted || examGraded;
  const startQuizDisabled =
    Number(ex?.enable) === 1 || Boolean(startsIn) || pastEnd;

  let markText = null;
  if (showMarkLine) {
    if (examGraded && scoreDenom != null && String(scoreDenom).trim() !== "") {
      markText = `Your score: ${ex.right_answers} / ${scoreDenom}`;
    } else if (examGraded) {
      markText = `Your score: ${ex.right_answers}`;
    } else {
      markText = "Mark: Pending";
    }
  }

  const subtitleParts = [formatExamMeta(ex)];
  if (pastEnd) subtitleParts.push("Ended");
  else {
    if (startsIn) subtitleParts.push(startsIn);
    if (closesIn) subtitleParts.push(closesIn);
  }
  if (markText) subtitleParts.push(markText);

  return (
    <RowShell
      icon={Number(ex?.type) === 0 ? MdQuiz : MdAssignment}
      title={itemTitle(ex)}
      subtitle={subtitleParts.join(" · ")}
    >
      {examSubmitted ? (
        canReviewAnswers ? (
          <Button as={Link} to={examResultPath} {...primaryBtnProps}>
            See answers
          </Button>
        ) : (
          <Button
            {...primaryBtnProps}
            isDisabled
            cursor="not-allowed"
            _disabled={{ bg: C.secondary, opacity: 0.55 }}
            _hover={{}}
          >
            See answers
          </Button>
        )
      ) : startQuizDisabled ? (
        <Button
          {...primaryBtnProps}
          isDisabled
          cursor="not-allowed"
          _disabled={{ bg: C.secondary, opacity: 0.55 }}
          _hover={{}}
        >
          {pastEnd ? "Ended" : "Start"}
        </Button>
      ) : (
        <Button as={Link} to={examGuidePath} {...primaryBtnProps}>
          Start
        </Button>
      )}

      {examSubmitted ? (
        isExamCorrected(ex) ? (
          <Button {...outlineBtnProps} onClick={() => onExamPreview(ex)}>
            Preview PDF
          </Button>
        ) : (
          <Button
            {...outlineBtnProps}
            isDisabled
            cursor="not-allowed"
            _hover={{}}
            _disabled={{ opacity: 0.55, cursor: "not-allowed" }}
          >
            Preview PDF
          </Button>
        )
      ) : null}

      {ex?.redo_flag == 1 ? (
        <Button
          {...outlineBtnProps}
          borderColor={C.secondary}
          color={C.secondary}
          isLoading={redoLoadingExamId === ex?.id}
          onClick={() => onRequestRedo(ex)}
          _hover={{ bg: `${C.secondary}12` }}
        >
          Request Redo
        </Button>
      ) : null}
    </RowShell>
  );
}

function HomeworkRow({ hw, disabled, onPrimary, onPreview }) {
  const statusLine = homeworkStatusSummary(hw);
  const label = homeworkActionLabel(hw);
  const subtitleParts = [formatHomeworkMeta(hw)];
  if (statusLine) subtitleParts.push(statusLine);

  const sh = hw?.student_homework;
  const showPreviewSecondary = sh?.status === 2 && sh?.file;
  const pdfUnlocked = isHomeworkCorrected(hw);
  const primaryOpensPdf = (sh?.status === 0 || sh?.status === 1) && Boolean(sh?.file);
  const primaryDisabled = disabled || (primaryOpensPdf && !pdfUnlocked);

  return (
    <RowShell icon={MdHomeWork} title={itemTitle(hw)} subtitle={subtitleParts.join(" · ")}>
      <Button
        {...primaryBtnProps}
        isDisabled={primaryDisabled}
        cursor={primaryDisabled ? "not-allowed" : "pointer"}
        _disabled={{ bg: C.secondary, opacity: 0.55 }}
        _hover={primaryDisabled ? {} : primaryBtnProps._hover}
        onClick={onPrimary}
      >
        {label}
      </Button>
      {showPreviewSecondary ? (
        <Button
          {...outlineBtnProps}
          isDisabled={disabled || !pdfUnlocked}
          cursor={disabled || !pdfUnlocked ? "not-allowed" : "pointer"}
          _hover={disabled || !pdfUnlocked ? {} : outlineBtnProps._hover}
          _disabled={{ opacity: 0.55, cursor: "not-allowed" }}
          onClick={onPreview}
        >
          Preview PDF
        </Button>
      ) : null}
    </RowShell>
  );
}

/**
 * Homepage card listing quizzes, exams, or homework in live-session row style.
 * @param {"quizzes"|"exams"|"homework"} type
 */
const HomeQuickLinksPanel = ({ type = "exams" }) => {
  const dispatch = useDispatch();
  const allExams = useSelector((s) => s.chapterExams.allExams) || [];
  const allHomeworks = useSelector((s) => s.chapterExams.allHomeworks) || [];
  const isLoading = useSelector((s) => s.chapterExams.isLoading);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [homeworkForModal, setHomeworkForModal] = useState(null);
  const [redoLoadingExamId, setRedoLoadingExamId] = useState(null);
  const panel = PANEL_CONFIG[type] || PANEL_CONFIG.exams;
  const isHomework = type === "homework";
  const [listTab, setListTab] = useState("all");

  const viewAllTo = panel.viewAllTo;
  const returnPath = viewAllTo;

  const refreshExams = useCallback(() => {
    dispatch(getAllExamsAndHomeworksRequest());
  }, [dispatch]);

  const quizItems = useMemo(
    () => sortByNewest(allExams.filter(isQuizType)),
    [allExams]
  );
  const examItems = useMemo(
    () => sortByNewest(allExams.filter(isFormalExamType)),
    [allExams]
  );
  const classifiedExams = useMemo(
    () => sortByNewest(allExams.filter(isHomeworkExamType)),
    [allExams]
  );

  const exams = type === "quizzes" ? quizItems : type === "exams" ? examItems : classifiedExams;
  const homeworks = isHomework ? allHomeworks : [];
  const hasAny = exams.length + homeworks.length > 0;
  const showLoading = isLoading && !hasAny;

  const recentResults = useMemo(() => {
    const examResults = exams
      .filter(hasRightAnswers)
      .map((ex) => ({ kind: "exam", item: ex, updated: updatedAtMs(ex) }));
    const homeworkResults = homeworks
      .filter(hasRightAnswers)
      .map((hw) => ({ kind: "homework", item: hw, updated: updatedAtMs(hw) }));
    return [...examResults, ...homeworkResults].sort((a, b) => b.updated - a.updated);
  }, [exams, homeworks]);

  const showingResults = listTab === "results";
  const visibleCount = showingResults ? recentResults.length : exams.length + homeworks.length;

  const Icon = panel.icon;

  const handleExamPreview = (ex) => {
    if (!isExamCorrected(ex)) return;
    const hasFile = ex?.student_pdf;
    if (!hasFile || String(hasFile).trim() === "") return;
    const url = examSubmissionPdfUrl(hasFile);
    if (url) {
      setPdfPreview({
        url,
        title: itemTitle(ex),
        backLabel: "Back",
      });
    }
  };

  const handleRequestRedo = (exam) => {
    setRedoLoadingExamId(exam?.id);
    const formData = new FormData();
    formData.append("exam_id", exam?.id);
    dispatch(examRedoRequest(formData, () => setRedoLoadingExamId(null)));
  };

  const handleHomeworkPrimary = (hw) => {
    const sh = hw?.student_homework;
    const hasFile = sh?.file;
    const status = sh?.status;
    if ((status === 0 || status === 1) && hasFile) {
      if (!isHomeworkCorrected(hw)) return;
      const url = homeworkSubmissionPdfUrl(hasFile);
      if (url) {
        setPdfPreview({
          url,
          title: itemTitle(hw),
          backLabel: "Back",
        });
      }
    } else if (status === 2 || !sh) {
      setHomeworkForModal(hw);
      dispatch(openSolveHomeworkModel());
    }
  };

  const handleHomeworkPreview = (hw) => {
    if (!isHomeworkCorrected(hw)) return;
    const hasFile = hw?.student_homework?.file;
    if (!hasFile) return;
    const url = homeworkSubmissionPdfUrl(hasFile);
    if (url) {
      setPdfPreview({
        url,
        title: itemTitle(hw),
        backLabel: "Back",
      });
    }
  };

  const homeworkDisabled = (hw) => hw?.active === 0 || hw?.enable === 0;

  return (
    <Box h="full" w="full" minW={0}>
      <Box
        p={{ base: 5, md: 6 }}
        bg={C.paper}
        borderWidth="1px"
        borderColor={C.border}
        borderRadius="lg"
        boxShadow="sm"
        w="full"
        minW={0}
        display="flex"
        flexDirection="column"
        maxH={{ base: "360px", md: "420px" }}
        h="full"
      >
        <Flex align="center" justify="space-between" gap={3} mb={4} flexShrink={0}>
          <Flex align="center" gap={3} minW={0}>
            <Flex
              w="36px"
              h="36px"
              borderRadius="md"
              bg="rgba(91, 13, 22, 0.1)"
              align="center"
              justify="center"
              color={C.primary}
              flexShrink={0}
            >
              <Icon size={20} />
            </Flex>
            <Box minW={0}>
              <Text
                fontSize="sm"
                fontWeight="800"
                letterSpacing="0.08em"
                color={C.primary}
                textTransform="uppercase"
                mb={0.5}
                sx={darkTitleSx}
              >
                {panel.eyebrow}
              </Text>
              <Text fontSize="xs" fontWeight="500" color={C.muted}>
                {panel.title}
              </Text>
            </Box>
          </Flex>
          <ChakraLink
            as={Link}
            to={viewAllTo}
            fontSize="9px"
            fontWeight="800"
            letterSpacing="0.08em"
            textTransform="uppercase"
            color={C.secondary}
            display="inline-flex"
            alignItems="center"
            gap={0.5}
            flexShrink={0}
            _hover={{ color: C.primary, textDecoration: "none" }}
          >
            View all
            <MdChevronRight size={16} />
          </ChakraLink>
        </Flex>

        {!showLoading ? (
          <Flex
            gap={1}
            mb={3}
            flexShrink={0}
            p="3px"
            bg={studentSurfaces.muted}
            borderRadius="md"
            w="fit-content"
            maxW="100%"
            flexWrap="wrap"
          >
            {[
              { id: "all", label: "All" },
              { id: "results", label: "Recent results" },
            ].map((tab) => {
              const active = listTab === tab.id;
              return (
                <Button
                  key={tab.id}
                  size="xs"
                  variant="unstyled"
                  h="26px"
                  px={3}
                  borderRadius="md"
                  fontSize="9px"
                  fontWeight="800"
                  letterSpacing="0.08em"
                  textTransform="uppercase"
                  color={active ? "white" : C.secondary}
                  bg={active ? C.secondary : "transparent"}
                  onClick={() => setListTab(tab.id)}
                  _hover={{ bg: active ? C.primary : "rgba(91, 13, 22, 0.06)" }}
                >
                  {tab.label}
                </Button>
              );
            })}
          </Flex>
        ) : null}

        {showLoading ? (
          <VStack align="stretch" spacing={2.5} flex="1" minH={0}>
            <Skeleton height="64px" borderRadius="md" />
            <Skeleton height="64px" borderRadius="md" />
            <Skeleton height="64px" borderRadius="md" />
          </VStack>
        ) : visibleCount === 0 ? (
          <Text fontSize="sm" color={C.muted} lineHeight="tall">
            {showingResults ? "No recent results yet." : panel.empty}
          </Text>
        ) : (
          <Box
            className="scroll"
            flex="1"
            minH={0}
            overflowY="auto"
            overflowX="hidden"
            pr={1}
            css={thinScrollCss}
          >
            <VStack align="stretch" spacing={2.5}>
              {showingResults
                ? recentResults.map((row) =>
                    row.kind === "exam" ? (
                      <ExamRow
                        key={`e-${row.item.id}`}
                        ex={row.item}
                        courseReturnPath={returnPath}
                        redoLoadingExamId={redoLoadingExamId}
                        onExamPreview={handleExamPreview}
                        onRequestRedo={handleRequestRedo}
                      />
                    ) : (
                      <HomeworkRow
                        key={`hw-${row.item.id}`}
                        hw={row.item}
                        disabled={homeworkDisabled(row.item)}
                        onPrimary={() => handleHomeworkPrimary(row.item)}
                        onPreview={() => handleHomeworkPreview(row.item)}
                      />
                    )
                  )
                : (
                  <>
                    {exams.map((ex) => (
                      <ExamRow
                        key={`e-${ex.id}`}
                        ex={ex}
                        courseReturnPath={returnPath}
                        redoLoadingExamId={redoLoadingExamId}
                        onExamPreview={handleExamPreview}
                        onRequestRedo={handleRequestRedo}
                      />
                    ))}
                    {homeworks.map((hw) => (
                      <HomeworkRow
                        key={`hw-${hw.id}`}
                        hw={hw}
                        disabled={homeworkDisabled(hw)}
                        onPrimary={() => handleHomeworkPrimary(hw)}
                        onPreview={() => handleHomeworkPreview(hw)}
                      />
                    ))}
                  </>
                )}
            </VStack>
          </Box>
        )}
      </Box>

      <PdfPreviewModal
        isOpen={Boolean(pdfPreview?.url)}
        onClose={() => setPdfPreview(null)}
        pdfUrl={pdfPreview?.url || ""}
        title={pdfPreview?.title}
        backLabel={pdfPreview?.backLabel}
        totalPages={pdfPreview?.totalPages}
      />
      <SolveHomework
        data={homeworkForModal}
        setSelectedData={setHomeworkForModal}
        onSubmitted={refreshExams}
      />
    </Box>
  );
};

export default HomeQuickLinksPanel;
